import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import engineeringMaterialService, {
  formatKg,
  type EngineeringCalcMethod,
  type ItemEngineeringBundle,
} from '@/services/engineeringMaterial.service';
import { useAuthStore } from '@/store/authStore';

const { Text } = Typography;

const METHOD_LABELS: Record<EngineeringCalcMethod, string> = {
  GEOMETRY: 'Geometry × density',
  PUBLISHED: 'Published section weight (kg/m)',
  FIXED: 'Fixed unit weight',
  MANUAL: 'Manual on each document',
};

interface Props {
  itemId: string;
  itemCode?: string | null;
  itemType?: string | null;
}

const errorText = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

export const ItemEngineeringDefinitionCard: React.FC<Props> = ({ itemId, itemCode, itemType }) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const perms = user?.permissions ?? [];
  const isAdmin = Boolean(user?.is_super_admin);
  const canApprove = isAdmin || perms.includes('inventory.engineering.approve');
  const canManage = canApprove || perms.includes('inventory.engineering.manage');
  const canView = canManage || perms.includes('inventory.engineering.view');

  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: config } = useQuery({
    queryKey: ['engineering', 'config'],
    queryFn: () => engineeringMaterialService.getConfig(),
    enabled: canView,
  });
  const bundleKey = ['engineering', 'item', itemId];
  const { data: bundle } = useQuery<ItemEngineeringBundle>({
    queryKey: bundleKey,
    queryFn: () => engineeringMaterialService.getItemDefinition(itemId),
    enabled: canView && Boolean(config?.available),
  });
  const { data: grades = [] } = useQuery({
    queryKey: ['engineering', 'grades', false],
    queryFn: () => engineeringMaterialService.listGrades(false),
    enabled: editing,
  });
  const { data: sections = [] } = useQuery({
    queryKey: ['engineering', 'sections', false],
    queryFn: () => engineeringMaterialService.listSections(false),
    enabled: editing,
  });
  const { data: approvedPreview } = useQuery({
    queryKey: ['engineering', 'item-preview', itemId, bundle?.approved?.revision],
    queryFn: () => engineeringMaterialService.calculate({ item_code: String(itemCode), quantity: 1, uom: 'PCS' }),
    enabled: Boolean(bundle?.approved && itemCode),
    retry: false,
  });

  const profileCode = Form.useWatch('profile_code', form) as string | undefined;
  const method = Form.useWatch('calc_method', form) as EngineeringCalcMethod | undefined;
  const profile = useMemo(() => config?.profiles.find((p) => p.code === profileCode), [config, profileCode]);
  const paramLabel = (p: string) => config?.param_labels?.[p] ?? p;
  const allParams = profile ? [...profile.params, ...profile.optional_params, 'length'] : [];

  const definition = bundle?.definition ?? null;

  useEffect(() => {
    if (!editing) return;
    if (definition) {
      form.setFieldsValue({
        profile_code: definition.profile_code,
        calc_method: definition.calc_method,
        material_grade_id: definition.material_grade_id ?? undefined,
        section_weight_id: definition.section_weight_id ?? undefined,
        engineering_standard: definition.engineering_standard ?? undefined,
        dims: definition.standard_dimensions ?? {},
        dimensions_variable: Boolean(definition.dimensions_variable),
        fixed_unit_weight: definition.fixed_unit_weight != null ? Number(definition.fixed_unit_weight) : undefined,
        fixed_weight_basis: definition.fixed_weight_basis ?? undefined,
        tolerance_plus_pct: definition.tolerance_plus_pct != null ? Number(definition.tolerance_plus_pct) : undefined,
        tolerance_minus_pct: definition.tolerance_minus_pct != null ? Number(definition.tolerance_minus_pct) : undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ calc_method: 'GEOMETRY', dimensions_variable: false });
    }
  }, [editing, definition?.id]);

  useEffect(() => {
    if (profile && method && !profile.methods.includes(method)) form.setFieldValue('calc_method', profile.methods[0]);
  }, [profile?.code]);

  if (!canView || !config?.available) return null;
  if (itemType && ['Service', 'Subcontract'].includes(itemType)) return null;

  const save = async () => {
    try {
      const v = await form.validateFields();
      setBusy(true);
      const dims: Record<string, number | null> = {};
      for (const p of allParams) {
        const val = v.dims?.[p];
        if (val != null && val !== '') dims[p] = Number(val);
      }
      await engineeringMaterialService.saveItemDefinition(itemId, {
        profile_code: v.profile_code,
        calc_method: v.calc_method,
        material_grade_id: v.calc_method === 'GEOMETRY' ? v.material_grade_id ?? null : null,
        section_weight_id: v.calc_method === 'PUBLISHED' ? v.section_weight_id ?? null : null,
        engineering_standard: v.engineering_standard ?? null,
        standard_dimensions: dims,
        dimensions_variable: Boolean(v.dimensions_variable),
        fixed_unit_weight: v.calc_method === 'FIXED' ? v.fixed_unit_weight ?? null : null,
        fixed_weight_basis: v.calc_method === 'FIXED' ? v.fixed_weight_basis ?? null : null,
        tolerance_plus_pct: v.tolerance_plus_pct ?? null,
        tolerance_minus_pct: v.tolerance_minus_pct ?? null,
      });
      message.success('Engineering definition saved as Draft');
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: bundleKey });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(errorText(e, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    try {
      setBusy(true);
      await engineeringMaterialService.approveItemDefinition(itemId);
      message.success('Engineering definition approved');
      await queryClient.invalidateQueries({ queryKey: ['engineering'] });
    } catch (e) {
      message.error(errorText(e, 'Approval failed'));
    } finally {
      setBusy(false);
    }
  };

  const approved = bundle?.approved ?? null;
  return (
    <Card
      title="Engineering & weight definition"
      style={{ marginTop: 16 }}
      extra={
        definition ? (
          <Space>
            <Tag color={definition.status === 'APPROVED' ? 'green' : 'gold'}>{definition.status}</Tag>
            {approved ? <Text type="secondary">Approved rev {approved.revision}</Text> : null}
          </Space>
        ) : null
      }
    >
      {!config.enabled && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Theoretical weight on Purchase Requests is switched off for this company. Definitions can still be prepared."
        />
      )}

      {approved && !editing && (
        <Descriptions size="small" column={{ xs: 1, md: 3 }} bordered style={{ marginBottom: 12 }}>
          <Descriptions.Item label="Profile">
            {config.profiles.find((p) => p.code === approved.profile_code)?.label ?? approved.profile_code}
          </Descriptions.Item>
          <Descriptions.Item label="Method">{METHOD_LABELS[approved.calc_method]}</Descriptions.Item>
          <Descriptions.Item label="Material">
            {approved.grade ? `${approved.grade.family} ${approved.grade.grade_code}` : approved.section?.designation ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Density">
            {approved.density_g_cm3 != null ? `${approved.density_g_cm3} g/cm³` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Standard dimensions (mm)">
            {Object.entries(approved.standard_dimensions || {})
              .map(([k, v]) => `${paramLabel(k)} ${v}`)
              .join(' · ') || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Theoretical weight">
            {approvedPreview?.snapshot.result.status === 'CALCULATED'
              ? [
                  approvedPreview.snapshot.result.kg_per_m != null
                    ? `${approvedPreview.snapshot.result.kg_per_m.toFixed(3)} kg/m`
                    : null,
                  `${formatKg(approvedPreview.theoretical_unit_weight)} per piece`,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Needs a piece length on the document'}
          </Descriptions.Item>
          <Descriptions.Item label="Tolerance">
            {approved.tolerance_plus_pct != null || approved.tolerance_minus_pct != null
              ? `+${approved.tolerance_plus_pct ?? 0}% / −${approved.tolerance_minus_pct ?? 0}%`
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Dimensions on documents">
            {approved.dimensions_variable ? 'May vary per line' : 'Locked to standard'}
          </Descriptions.Item>
        </Descriptions>
      )}

      {definition?.status === 'DRAFT' && approved && !editing && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`A draft change is waiting for approval. New documents keep using approved revision ${approved.revision}.`}
        />
      )}

      {!definition && !editing && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          No engineering definition. Add one to show theoretical weights on Purchase Requests for this item.
        </Text>
      )}

      {editing ? (
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="profile_code" label="Profile" rules={[{ required: true }]}>
                <Select options={config.profiles.map((p) => ({ value: p.code, label: p.label }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="calc_method" label="Calculation method" rules={[{ required: true }]}>
                <Select
                  options={(profile?.methods ?? config.calc_methods).map((m) => ({ value: m, label: METHOD_LABELS[m] }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="engineering_standard" label="Engineering standard">
                <Input placeholder="e.g. EN 10219, ASME B36.10" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          {method === 'GEOMETRY' && (
            <Form.Item
              name="material_grade_id"
              label="Material grade"
              rules={[{ required: true, message: 'Select an approved grade' }]}
              extra="Only approved grades can be used when approving the definition."
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={grades.map((g) => ({
                  value: g.id,
                  label: `${g.family} ${g.grade_code} — ${Number(g.density_g_cm3)} g/cm³${g.status !== 'APPROVED' ? ' (draft)' : ''}`,
                }))}
              />
            </Form.Item>
          )}
          {method === 'PUBLISHED' && (
            <Form.Item name="section_weight_id" label="Published section" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={sections
                  .filter((s) => s.profile_code === profileCode)
                  .map((s) => ({
                    value: s.id,
                    label: `${s.designation} — ${Number(s.kg_per_m)} kg/m${s.standard ? ` (${s.standard})` : ''}${
                      s.status !== 'APPROVED' ? ' (draft)' : ''
                    }`,
                  }))}
              />
            </Form.Item>
          )}
          {method === 'FIXED' && (
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="fixed_unit_weight" label="Fixed unit weight (kg)" rules={[{ required: true }]}>
                  <InputNumber min={0.000001} precision={6} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="fixed_weight_basis" label="Per" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { value: 'PER_PIECE', label: 'Piece' },
                      { value: 'PER_METRE', label: 'Metre' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {profile && method !== 'MANUAL' && (
            <>
              <Text strong>Standard dimensions (mm)</Text>
              <Row gutter={16} style={{ marginTop: 8 }}>
                {allParams.map((p) => (
                  <Col xs={12} md={6} key={p}>
                    <Form.Item
                      name={['dims', p]}
                      label={`${paramLabel(p)}${profile.params.includes(p) ? '' : ' (optional)'}`}
                    >
                      <InputNumber min={0} precision={3} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Leave length empty when it is given on each Purchase Request line.
              </Text>
            </>
          )}
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="dimensions_variable"
                label="Section dimensions may vary per document line"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="tolerance_plus_pct" label="Weight tolerance + %">
                <InputNumber min={0} max={100} precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="tolerance_minus_pct" label="Weight tolerance − %">
                <InputNumber min={0} max={100} precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button type="primary" loading={busy} onClick={save}>
              Save as draft
            </Button>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
          </Space>
        </Form>
      ) : (
        <Space>
          {canManage && (
            <Button onClick={() => setEditing(true)}>{definition ? 'Revise definition' : 'Add engineering definition'}</Button>
          )}
          {canApprove && definition?.status === 'DRAFT' && (
            <Popconfirm
              title="Approve this engineering definition?"
              description="Density and section weight are frozen into a new revision. Saved documents are not changed."
              onConfirm={approve}
            >
              <Button type="primary" loading={busy}>
                Approve
              </Button>
            </Popconfirm>
          )}
        </Space>
      )}
    </Card>
  );
};

export default ItemEngineeringDefinitionCard;
